import { Button, FormControl, Grid, SvgIcon, TextField, MenuItem, Select, InputLabel, SelectChangeEvent } from '@mui/material';
import ScheduleTable from './ScheduleTable';
import { StockCheckScheduleDetail } from '../../../app/services/api/generated';
import { useContext, useState } from 'react';
import LoadingWheel from '../../../components/ui/LoadingWheel';
import { PlusCircle } from '../../../assets';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../components/shared/useUser';
import { StockCheckType, StockCheckTypeLabels } from '../../../enums/StockCheckType';
import { ReactNode } from 'react';

const MainBox = (props: { schedules: StockCheckScheduleDetail[], storeID: string, loadData: (allStores?: boolean) => void, showAllStores: boolean, setShowAllStores: (value: boolean) => void }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { t } = useTranslation('common');
  const { hasPermissionTo } = useContext(UserContext);
  const navigate = useNavigate();
  const hasRightToEdit: boolean = hasPermissionTo(['StockCountScheduleWrite']) || hasPermissionTo(['StockCountAdmin']);
  const [selectedType, setSelectedType] = useState<number>(0);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };
  const handleTypeChange = (event: SelectChangeEvent<StockCheckType | ''>, child: ReactNode) => {
    setSelectedType(event.target.value as StockCheckType);
  };

  const handleStoreFilterChange = (event: SelectChangeEvent<string>) => {
    const isAllStores = event.target.value === 'all';
    props.setShowAllStores(isAllStores);
    props.loadData(isAllStores ? true : undefined);
  };

  return <Grid container
    sx={{
      pt: '8px',
    }} >
    <Grid item
      xs={8}
      sx={{ pb: '8px' }}>
      <Grid container
        spacing={5}>
        <Grid item>
          <FormControl fullWidth>
            <TextField
              label={t('search') + ':'}
              size="small"
              id="input-with-icon-textfield"
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </FormControl>
        </Grid>
        <Grid item
          lg={4}
          xs={6}>
          <FormControl fullWidth
            size="small">
            <InputLabel>{t('stockcounts.type')}</InputLabel>
            <Select
              value={selectedType}
              onChange={handleTypeChange}
              label={t('stockcounts.type')}
            >
              {Object.values(StockCheckType)
                .filter(value => typeof value === 'number')
                .map(type => (
                  <MenuItem key={type}
                    value={type}>
                    {StockCheckTypeLabels[type as StockCheckType]}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item
          lg={4}
          xs={6}>
          <FormControl fullWidth
            size="small">
            <InputLabel>{t('stockcounts.store')}</InputLabel>
            <Select
              value={props.showAllStores ? 'all' : 'current'}
              onChange={handleStoreFilterChange}
              label={t('stockcounts.store')}
            >
              <MenuItem value="current">{t('stockcounts.currentStore')}</MenuItem>
              <MenuItem value="all">{t('stockcounts.allStores')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Grid>

    <Grid item
      xs={4}
      sx={{ display: 'flex', justifyContent: 'flex-end' }}>
      <Button
        size="lg"
        sx={{ ml: 4 }}
        onClick={() => navigate('/create-edit-schedule-stock-count')}
        startIcon={
          <SvgIcon>
            <PlusCircle />
          </SvgIcon>
        }
        disabled={!hasRightToEdit}
      >
        {t('createNew')}
      </Button>
    </Grid>

    <Grid item
      xs={12}>
      <ScheduleTable
        list={props.schedules}
        storeID={props.storeID}
        loadData={() => props.loadData(props.showAllStores ? true : undefined)}
        searchTerm={searchTerm}
        selectedType={selectedType}
        showAllStores={props.showAllStores}
      />
    </Grid>
  </Grid>;
};

const StockCountSchedule = (props: { schedules: StockCheckScheduleDetail[], storeID: string, loadData: (allStores?: boolean) => void, isLoading: boolean, showAllStores: boolean, setShowAllStores: (value: boolean) => void }) => {
  return props.isLoading ? <LoadingWheel></LoadingWheel> : <MainBox schedules={props.schedules}
    storeID={props.storeID}
    loadData={props.loadData}
    showAllStores={props.showAllStores}
    setShowAllStores={props.setShowAllStores}></MainBox>;
};

export default StockCountSchedule;